import { z } from 'zod';
import { idSchema, phoneSchema } from './common.js';
/* -------------------------------------------------------------------------- */
/* Staff login (audience `pospak-web`)                                         */
/* -------------------------------------------------------------------------- */
export const loginSchema = z.object({
    businessCode: z.string().trim().min(1).max(32),
    username: z.string().trim().min(1).max(64),
    password: z.string().min(1).max(256),
    totpCode: z.string().regex(/^\d{6}$/).optional(),
});
/** Till PIN login — fast cashier switching on a shared terminal. */
export const pinLoginSchema = z.object({
    businessCode: z.string().trim().min(1).max(32),
    shopId: idSchema,
    username: z.string().trim().min(1).max(64),
    pin: z.string().regex(/^\d{4,8}$/),
});
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
/** Policy mirrors ../lims's `passwordSchema`: min 10, upper/lower/digit/symbol. */
export const passwordSchema = z
    .string()
    .min(10, 'Password must be at least 10 characters')
    .max(256)
    .regex(/[a-z]/, 'Password must contain a lowercase letter')
    .regex(/[A-Z]/, 'Password must contain an uppercase letter')
    .regex(/[0-9]/, 'Password must contain a digit')
    .regex(/[^A-Za-z0-9]/, 'Password must contain a symbol');
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1).max(256),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(10).max(256),
})
    .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
})
    .refine((v) => v.newPassword !== v.currentPassword, {
    message: 'The new password must be different',
    path: ['newPassword'],
});
export const setPinSchema = z.object({
    pin: z.string().regex(/^\d{4,8}$/),
    currentPassword: z.string().min(1).max(256),
});
export const totpEnrollSchema = z.object({
    code: z.string().regex(/^\d{6}$/),
});
export const switchShopSchema = z.object({
    shopId: idSchema,
});
/* -------------------------------------------------------------------------- */
/* Owner portal (audience `pospak-owner`) — email + TOTP, no password          */
/* -------------------------------------------------------------------------- */
export const ownerRequestCodeSchema = z.object({
    email: z.string().trim().email().max(200),
});
/**
 * Two explicit steps, not inferred from whether `totpCode` happens to be
 * present — a login is either `step: 'email'` (email-only: look the owner
 * up and tell the caller what screen to show next, never touches
 * `totpCode` at all even if one were sent) or `step: 'code'` (the real
 * sign-in: `totpCode` is required and actually checked). Splitting these
 * into a named `step` instead of overloading "is totpCode missing?" removes
 * the ambiguity that let a `step: 'email'` request ever reach the TOTP
 * check in the first place. See `apps/api/src/modules/owner/index.ts`'s
 * `/login` handler.
 */
export const ownerVerifySchema = z.object({
    email: z.string().trim().email().max(200),
    step: z.enum(['email', 'code']).default('email'),
    totpCode: z.string().regex(/^\d{6}$/, 'TOTP code must be 6 digits').optional(),
});
/* -------------------------------------------------------------------------- */
/* Public portal customer OTP (audience `pospak-portal`)                       */
/* -------------------------------------------------------------------------- */
export const otpRequestSchema = z.object({
    phone: phoneSchema,
    shopSlug: z.string().trim().min(1).max(120),
});
export const otpVerifySchema = z.object({
    phone: phoneSchema,
    code: z.string().regex(/^\d{4,6}$/),
});
//# sourceMappingURL=auth.js.map